import React, { useEffect } from 'react'
import { IconShare3 } from '@tabler/icons-react'
import { Field, Formik } from 'formik'
import * as Yup from 'yup'

import { CopyButton } from '@/components/buttons/CopyButton'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { Checkbox } from '@/components/forms/formik'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'

import './AudioShareButton.scss'

export const AudioShareButton = ({ callId, progress, duration }) => {
  const initialUrl = `${window.origin}/calls/${callId}`
  const maxMinutes = Math.floor(duration / 60)
  const maxRemainingSeconds = Math.floor(duration % 60)

  const getCurrentMinutesAndSecondsFromProgress = () => {
    if (!progress) {
      return { minutes: 0, seconds: 0 }
    }

    // Convert progress in seconds to minutes and seconds
    const totalSeconds = Math.floor(progress)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = Math.floor(totalSeconds % 60)

    return { minutes, seconds }
  }

  const getValidationSchema = () =>
    Yup.object().shape({
      minutes: Yup.number()
        .min(0, 'Enter a valid number')
        .max(maxMinutes, `Can not be greater than length of call`),
      seconds: Yup.number()
        .min(0, 'Enter a valid number')
        .max(duration, `Can not be greater than length of call`)
        .when('minutes', {
          is: (val) => val >= maxMinutes,
          then: Yup.number().max(maxRemainingSeconds, `Can not be greater than length of call`),
        }),
    })

  return (
    <ButtonAndFormModal
      icon={<IconShare3 />}
      buttonLabel="Share"
      modalTitle="Share"
      modalId={`callExplorer/share/${callId}`}
      modalProps={{ size: 'tiny' }}
      buttonProps={{ small: true }}
      form={
        <Formik
          initialValues={{
            url: initialUrl,
            enableTimestamp: true,
            ...getCurrentMinutesAndSecondsFromProgress(),
          }}
          validationSchema={getValidationSchema()}
        >
          {({ values, errors, setFieldValue }) => {
            useEffect(() => {
              if (values.enableTimestamp) {
                const totalSeconds =
                  (Number(values.minutes) || 0) * 60 + (Number(values.seconds) || 0)
                const urlWithTimestamp =
                  totalSeconds > 0 ? `${initialUrl}?timestamp=${totalSeconds}` : initialUrl

                setFieldValue('url', urlWithTimestamp)
              } else {
                // Reset when enableTimestamp is unchecked
                setFieldValue('url', initialUrl)
                setFieldValue('minutes', 0)
                setFieldValue('seconds', 0)
              }
            }, [values.minutes, values.seconds, values.enableTimestamp])

            return (
              <div className="field-stack">
                <FormField inline>
                  <Field as={TextInput} name="url" type="text" data-testid="textField-url" />
                  <CopyButton content={values.url} size="default" />
                </FormField>

                <section className="timestamp-grid">
                  <Field label="Start at:" name="enableTimestamp" component={Checkbox} inline />

                  <div className="flex-align-start small-gap">
                    <FormField error={errors.minutes} className="timestamp-field">
                      <label htmlFor="minutes">Minutes:</label>
                      <Field
                        as={TextInput}
                        data-testid="textField-minutes"
                        id="minutes"
                        name="minutes"
                        min="0"
                        type="number"
                        onKeyDown={(event) =>
                          ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault()
                        }
                      />
                    </FormField>

                    <FormField error={errors.seconds} className="timestamp-field">
                      <label htmlFor="seconds">Seconds:</label>
                      <Field
                        as={TextInput}
                        data-testid="textField-seconds"
                        id="seconds"
                        name="seconds"
                        min="0"
                        type="number"
                        onKeyDown={(event) =>
                          ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault()
                        }
                      />
                    </FormField>
                  </div>

                  <div className="timestamp-error">
                    {[...new Set(Object.values(errors))].map((error) => (
                      <ErrorMessage content={error} key={error} style={{ marginTop: 0 }} />
                    ))}
                  </div>
                </section>
              </div>
            )
          }}
        </Formik>
      }
    />
  )
}
